import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Info } from 'lucide-react';
import { Button, TopicCard, ChallengeCard, LoadingSpinner, Modal } from '../components';
import { useProgress } from '../hooks/useProgress';
import challengesData from '../data/challenges.json';

export default function TopicSelect() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { getTopicProgress, isChallengeCompleted, markIntroSeen, hasSeenIntro, isLoaded } = useProgress();
  const [showIntro, setShowIntro] = useState(false);

  const selectedTopic = topicId ? challengesData.topics.find(w => w.id === topicId) : null;

  // Show intro for new topic
  useEffect(() => {
    if (selectedTopic && !hasSeenIntro(selectedTopic.id)) {
      setShowIntro(true);
    }
  }, [selectedTopic, hasSeenIntro]);

  const handleCloseIntro = () => {
    if (selectedTopic) {
      markIntroSeen(selectedTopic.id);
    }
    setShowIntro(false);
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  // Show topic list
  if (!selectedTopic) {
    return (
      <div className="min-h-screen">
        {/* Header */}
        <header className="p-4 border-b border-[#2a2d31]">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" pill={false}>
                <Home size={18} />
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-game-text">Select a Topic</h1>
          </div>
        </header>

        {/* Topic grid */}
        <main className="max-w-6xl mx-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challengesData.topics.map((topic, index) => {
              const topicProgress = getTopicProgress(topic.id);
              const challenges = challengesData.challenges[topic.id] || [];
              const completedCount = topicProgress.completed?.length || 0;

              // All topics are always unlocked
              const isLocked = false;

              return (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  completion={topicProgress.completion || 0}
                  totalChallenges={challenges.length}
                  completedChallenges={completedCount}
                  isLocked={isLocked}
                />
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  // Show challenges for selected topic
  const challenges = challengesData.challenges[selectedTopic.id] || [];
  const topicProgress = getTopicProgress(selectedTopic.id);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="p-4 border-b border-[#2a2d31]">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" pill={false} onClick={() => navigate('/topics')}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-game-text">{selectedTopic.name}</h1>
            <p className="text-sm text-game-text-dim">{selectedTopic.description}</p>
          </div>
          <Button variant="ghost" size="sm" pill={false} onClick={() => setShowIntro(true)}>
            <Info size={18} />
          </Button>
        </div>
      </header>

      {/* Challenges grid */}
      <main className="max-w-6xl mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <span className="text-game-text-dim font-medium">
            {topicProgress.completed?.length || 0} / {challenges.length} completed
          </span>
          <span className="text-game-accent font-bold">
            {topicProgress.completion || 0}% complete
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {challenges.map((challenge, index) => {
            const isCompleted = isChallengeCompleted(selectedTopic.id, challenge.id);

            // Challenges are unlocked sequentially
            const prevChallenge = index > 0 ? challenges[index - 1] : null;
            const isLocked = prevChallenge && !isChallengeCompleted(selectedTopic.id, prevChallenge.id);

            return (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                topicId={selectedTopic.id}
                index={index}
                isCompleted={isCompleted}
                isLocked={isLocked}
              />
            );
          })}
        </div>
      </main>

      {/* Topic intro modal */}
      <Modal
        isOpen={showIntro}
        onClose={handleCloseIntro}
        title={`About: ${selectedTopic.name}`}
        size="responsive"
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-game-primary mb-1.5 uppercase tracking-wide">Overview</h3>
            <p className="text-game-text">{selectedTopic.description}</p>
          </div>

          {selectedTopic.intro && (
            <div>
              <h3 className="text-sm font-semibold text-game-primary mb-1.5 uppercase tracking-wide">Why Learn This?</h3>
              <div className="p-4 bg-[#2a2d31] rounded-xl">
                <p className="text-game-text-dim whitespace-pre-line">{selectedTopic.intro}</p>
              </div>
            </div>
          )}

          {selectedTopic.keyConcepts && selectedTopic.keyConcepts.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-game-primary mb-1.5 uppercase tracking-wide">Key Concepts</h3>
              <ul className="space-y-1.5">
                {selectedTopic.keyConcepts.map((concept, i) => {
                  const [title, ...rest] = concept.split(' - ');
                  const description = rest.join(' - ');
                  return (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-game-primary mt-1 flex-shrink-0">&#8226;</span>
                      <span>
                        <span className="text-game-text font-medium">{title}</span>
                        {description && <span className="text-game-text-dim"> - {description}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {selectedTopic.examples && selectedTopic.examples.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-game-primary mb-1.5 uppercase tracking-wide">Examples</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedTopic.examples.map((example, i) => (
                  <div key={i} className="p-3 bg-[#2a2d31] rounded-xl overflow-hidden">
                    <code
                      className="text-xs font-mono text-[#C2A528] block mb-1"
                      style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    >{example.code}</code>
                    <span className="text-xs text-game-text-dim">{example.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 flex justify-end">
            <Button onClick={handleCloseIntro}>
              {hasSeenIntro(selectedTopic.id) ? 'Close' : 'Start Learning'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
